import { useCallback, useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { useSelector } from "react-redux";
import {
    Button,
    Chip,
    CircularProgress,
    Container,
    FormControl,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Popup from "../../../components/Popup";
import { getSchoolIdForUser } from "../../../utils/userContext";
import { fetchCertificates, upsertCertificate } from "../../../utils/manebApi";

const STATUSES = ["Pending", "Issued", "Collected"];

const ManebCertificates = () => {
    const { currentUser } = useSelector((s) => s.user);
    const schoolId = getSchoolIdForUser(currentUser);

    const [loading, setLoading] = useState(true);
    const [rows, setRows] = useState([]);
    const [filter, setFilter] = useState("");
    const [showPopup, setShowPopup] = useState(false);
    const [message, setMessage] = useState("");

    const load = useCallback(async () => {
        if (!schoolId) return;
        setLoading(true);
        try {
            setRows(await fetchCertificates(schoolId, filter || undefined));
        } catch (err) {
            setMessage(err.response?.data?.message || "Could not load certificates");
            setShowPopup(true);
        } finally {
            setLoading(false);
        }
    }, [schoolId, filter]);

    useEffect(() => {
        load();
    }, [load]);

    const updateStatus = async (row, status) => {
        try {
            await upsertCertificate({
                id: row.id || row._id,
                schoolId,
                studentId: row.studentId,
                examType: row.examType,
                examYear: row.examYear,
                certificateNo: row.certificateNo,
                resultGrade: row.resultGrade,
                resultSummary: row.resultSummary,
                status,
            });
            await load();
        } catch (err) {
            setMessage(err.response?.data?.message || "Update failed");
            setShowPopup(true);
        }
    };

    return (
        <Container maxWidth="lg" sx={{ py: 3 }}>
            <Stack spacing={2}>
                <Button component={RouterLink} to="/Admin/maneb" startIcon={<ArrowBackIcon />} sx={{ alignSelf: "flex-start" }}>
                    MANEB hub
                </Button>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                    Certificate tracking
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Track PSLCE, JCE, and MSCE certificate issuance and collection after MANEB releases results.
                </Typography>

                <FormControl size="small" sx={{ maxWidth: 200 }}>
                    <InputLabel>Exam filter</InputLabel>
                    <Select label="Exam filter" value={filter} onChange={(e) => setFilter(e.target.value)}>
                        <MenuItem value="">All</MenuItem>
                        <MenuItem value="PSLCE">PSLCE</MenuItem>
                        <MenuItem value="JCE">JCE</MenuItem>
                        <MenuItem value="MSCE">MSCE</MenuItem>
                    </Select>
                </FormControl>

                {loading ? (
                    <CircularProgress />
                ) : (
                    <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "auto" }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Student</TableCell>
                                    <TableCell>Exam</TableCell>
                                    <TableCell>Year</TableCell>
                                    <TableCell>Grade</TableCell>
                                    <TableCell>Certificate no.</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {rows.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={7}>
                                            <Typography color="text.secondary" sx={{ py: 2 }}>
                                                No certificate records. Process mock results from a session or add manually.
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    rows.map((r) => (
                                        <TableRow key={r.id || r._id}>
                                            <TableCell>{r.student?.name}</TableCell>
                                            <TableCell>
                                                <Chip label={r.examType} size="small" />
                                            </TableCell>
                                            <TableCell>{r.examYear}</TableCell>
                                            <TableCell>{r.resultGrade}</TableCell>
                                            <TableCell>
                                                <TextField
                                                    size="small"
                                                    defaultValue={r.certificateNo}
                                                    onBlur={(e) =>
                                                        upsertCertificate({
                                                            id: r.id || r._id,
                                                            schoolId,
                                                            studentId: r.studentId,
                                                            examType: r.examType,
                                                            examYear: r.examYear,
                                                            certificateNo: e.target.value,
                                                            resultGrade: r.resultGrade,
                                                            resultSummary: r.resultSummary,
                                                            status: r.status,
                                                        }).then(load)
                                                    }
                                                />
                                            </TableCell>
                                            <TableCell>{r.status}</TableCell>
                                            <TableCell>
                                                {STATUSES.filter((s) => s !== r.status).map((s) => (
                                                    <Button key={s} size="small" onClick={() => updateStatus(r, s)}>
                                                        Mark {s}
                                                    </Button>
                                                ))}
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </Paper>
                )}
            </Stack>
            <Popup showPopup={showPopup} setShowPopup={setShowPopup} title="Certificates">
                <Typography>{message}</Typography>
            </Popup>
        </Container>
    );
};

export default ManebCertificates;
